import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, Loader, Plus } from 'lucide-react';
import './App.css';

// Components
import Message from './components/Message';
import SystemStatus from './components/SystemStatus';
import TypingIndicator from './components/TypingIndicator';
import ChatInput from './components/ChatInput';
import ModelSelector from './components/ModelSelector';

// Utils
import { formatText, WELCOME_MESSAGES } from './utils/textFormatter';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function App() {
  // State
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [systemStatus, setSystemStatus] = useState({ status: 'unknown', message: '', knowledge_base_stats: null });
  const [conversationId, setConversationId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedModel, setSelectedModel] = useState('claude');
  const [, setConversationStarted] = useState(false);
  const [, setAllConversations] = useState([]);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  // Check system health
  const checkSystemHealth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/health`);
      setSystemStatus(response.data);
    } catch (error) {
      setSystemStatus({
        status: 'error',
        message: 'No se puede conectar con el servidor',
        knowledge_base_stats: null
      });
    }
  }, []);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/conversations`);
      setAllConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error cargando conversaciones:', error);
    }
  }, []);

  // Start new conversation
  const startNewConversation = useCallback(async (model = 'claude') => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${API_BASE_URL}/conversation/start`, { model });
      const newConvId = response.data.conversation_id;
      const welcomeMsg = response.data.welcome_message;

      setConversationId(newConvId);
      setMessages([{
        type: 'assistant',
        content: welcomeMsg || WELCOME_MESSAGES[model],
        timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        isWelcome: true,
        model: model
      }]);
      setConversationStarted(true);
      setSelectedModel(model);

      await loadConversations();
    } catch (error) {
      console.error('Error iniciando conversación:', error);
      setMessages([{
        type: 'error',
        content: 'Error al conectar con el servidor. Verifica que esté ejecutándose.',
        timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [loadConversations]);

  // Initialize
  useEffect(() => {
    checkSystemHealth();
    loadConversations();
    startNewConversation(selectedModel);
  }, []);

  // Send message
  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading || !conversationId) return;

    const userMessage = {
      type: 'user',
      content: inputValue,
      timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setIsTyping(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/conversation/message`, {
        conversation_id: conversationId,
        message: inputValue,
        model: selectedModel
      });

      setIsTyping(false);

      const assistantMessage = {
        type: 'assistant',
        content: response.data.response,
        timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        sources: response.data.sources || [],
        model: selectedModel
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      setIsTyping(false);
      console.error('Error enviando mensaje:', error);

      const errorMessage = {
        type: 'error',
        content: 'Error al procesar tu consulta. Por favor, intenta de nuevo.',
        timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear conversation
  const clearConversation = () => {
    startNewConversation(selectedModel);
  };

  return (
    <div className="app-container">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <h1 className="app-title">SentencIA</h1>
          <div className="header-actions">
            <ModelSelector
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              showModelSelector={showModelSelector}
              setShowModelSelector={setShowModelSelector}
              isLoading={isLoading}
              isNavbar={true}
            />
            <button
              onClick={clearConversation}
              className="new-conversation-button"
              disabled={isLoading}
              title="Nueva conversación"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* Messages */}
        <div className="messages-container">
          {systemStatus.status === 'error' && (
            <div className="system-alert error">
              <Loader className="animate-spin" />
              <p>Conectando con el servidor...</p>
            </div>
          )}

          {messages
            .filter(message => !(message.isWelcome && messages.length > 1))
            .map((message, index) => (
              <Message
                key={index}
                message={message}
                formatText={formatText}
              />
            ))
          }

          {isTyping && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

      </main>
      
      {/* Input */}
      <ChatInput
        inputValue={inputValue}
        setInputValue={setInputValue}
        isLoading={isLoading}
        conversationId={conversationId}
        onSendMessage={sendMessage}
        onNewConversation={clearConversation}
      />
    </div>
  );
}

export default App;
